import * as yup from 'yup';
import { createI18n } from 'vue-i18n';
import messages from '../i18n';
import { isOverMinimumAge, GLOBAL_MIN_AGE_YEARS } from '@/services/GeneralService';

const i18n = createI18n({
  locale: 'en',
  legacy: false,
  messages,
  globalInjection: true,
});
const { t } = i18n.global;

/**
 * Exports
 */
export interface IAddress {
  id: string;
  center: string[];
  context: object[];
  place_name: string;
}

export interface IUser {
  id?: string;
  firstName: string;
  lastName?: string;
  email: string;
  phone?: string;
  lang?: string;
  orgName?: string; // most recent registered organization in case they have multiple
  zipCode: string;
  orgContactId?: string; // most recent registered organization contact id in case they have multiple--indicates user is an educator/contact
  orgContactRole?: string;
  dob?: string | null; // date of birth
  isStaff?: boolean; // indicates if user is a Unify America staff member
  isSuperuser?: boolean; // indicates if user is a Unify America admin user (typically for the tech team)
}

// This interface is a DTO for only including anonymous user demographics data for reporting purposes
// It should not include any personally identifiable information (PII) like name, email, etc
export interface IUserDemographicsDTO {
  gender? : string;
  age?: number; // Calculated age based on dob
  votingRecord?: string;
  politicalSpectrum?: string;
  race?: string[];
}

export const AddressObject: IAddress = {
  id: '',
  center: [],
  context: [],
  place_name: '',
};

export const UserObject: IUser = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  lang: 'en-US',
  zipCode: '',
  orgContactRole: '',
  dob: '',
  isStaff: false,
  isSuperuser: false,
};

// Base schema for all user registrations (open challenge)
export const UserRegistrationObjValidation = yup.object().shape({
  firstName: yup.string()
    .required(t('validation.firstNameRequired'))
    .max(30),
  lastName: yup.string()
    .required(t('validation.lastNameRequired'))
    .max(30),
  email: yup.string()
    .email(t('validation.emailInvalid'))
    .required(t('validation.emailRequired')),
  isEmailNewsletter: yup.boolean()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  politicalSpectrum: yup.string()
    .required(t('validation.politicalSpectrumRequired')),
  votingRecord: yup.string()
    .required(t('validation.votingRecordRequired')),
  isStudent: yup.boolean()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  howHeard: yup.array()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  zipCode: yup.string()
    .min(5)
    .max(10)
    .matches(/^\d{5}(-\d{4})?$/, t('validation.zipCodeInvalid')), // Regex for US zip code
  urbanRural: yup.string()
    .required(t('validation.urbanRuralRequired')),
  dob: yup.date()
    .typeError(t('validation.dobRequired'))
    .required(t('validation.dobRequired'))
    .test('not-in-future', t('validation.dobInFuture'), (dob) => {
      if (!dob) return true;
      return dob <= new Date();
    })
    .test('over-minimum-age', t('validation.dobTooYoung', { age: GLOBAL_MIN_AGE_YEARS }), (dob) => {
      if (!dob) return true;
      return isOverMinimumAge(dob);
    }),
  gender: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  race: yup.array().of(yup.string())
    .required(t('validation.raceRequired'))
    .min(1, t('validation.raceRequired'))
    .typeError(t('validation.raceRequired')),
  sharedGoals: yup.boolean()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  sharePointOfView: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  futureOfDemocracy: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  impactOfConversation: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  genderDescription: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .max(300),
  choiceForAmerica: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .required(t('validation.opinionRequired')),
  healthcareInAmerica: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .required(t('validation.opinionRequired')),
  trustOpposingViewsGoodReason: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  trustDifferentViewsCareToMakeCountryBetter: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  futureOfAmerica: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  faceToFaceConversation: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  feelingsOnFaceToFaceConversation: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  differentPerspectiveMisinformation: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value),
  isConsent: yup.boolean()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .oneOf([true], t('validation.consentRequired'))
    .required(t('validation.consentRequired')),
  isRecord: yup.boolean()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .when('dob', {
      is: (dob: Date | null) => {
        if (!dob) return false;
        return !isOverMinimumAge(dob, 18);
      },
      then: (schema) => schema.oneOf([false, undefined], t('validation.mustBe18ForRecording')),
      otherwise: (schema) => schema,
    }),
  orgURL: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .max(200),
  externalId: yup.string()
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .max(100),
});

// Extended schema for student registrations (college bowl)
export const unifyChallengeRegistrationObjValidation = UserRegistrationObjValidation.shape({
  intendedCollegeMajor: yup.string()
    .required(t('validation.intendedCollegeMajorRequired')),
  intendedCollegeMajorDescription: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .max(50)
    .when('intendedCollegeMajor', {
      is: 'Other',
      then: (schema) => schema.required(t('validation.intendedCollegeMajorDescriptionRequired')),
    }),
  confidenceDiscussingComplexIssues: yup.string()
    .required(t('validation.confidenceDiscussingComplexIssuesRequired')),
});

// Extended schema for student registrations (deep dive)
export const deepDiveRegistrationObjValidation = UserRegistrationObjValidation.shape({
  intendedCollegeMajor: yup.string()
    .required(t('validation.intendedCollegeMajorRequired')),
  intendedCollegeMajorDescription: yup.string()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .max(50)
    .when('intendedCollegeMajor', {
      is: 'Other',
      then: (schema) => schema.required(t('validation.intendedCollegeMajorDescriptionRequired')),
    }),
  confidenceDiscussingComplexIssues: yup.string()
    .required(t('validation.confidenceDiscussingComplexIssuesRequired')),
});

// Schema for updating user profile (does not require all fields to be present; only changed fields will be sent to backend)
export const UserUpdateObjValidation = yup.object().shape({
  firstName: yup.string()
    .min(1, t('validation.firstNameRequired'))
    .max(30),
  lastName: yup.string()
    .min(1, t('validation.lastNameRequired'))
    .max(30),
  email: yup.string()
    .min(5, t('validation.emailInvalid'))
    .email(t('validation.emailInvalid')),
  zipCode: yup.string()
    .min(5, t('validation.zipCodeInvalid'))
    .max(10, t('validation.zipCodeInvalid'))
    .matches(/^\d{5}(-\d{4})?$/, t('validation.zipCodeInvalid')), // Regex for US zip code
  dob: yup.date()
    .nullable()
    .typeError(t('validation.dobRequired'))
    .test('not-null', t('validation.dobRequired'), (dob) => dob !== null)
    .test('not-in-future', t('validation.dobInFuture'), (dob) => {
      if (!dob) return true;
      return dob <= new Date();
    })
    .test('over-minimum-age', t('validation.dobTooYoung', { age: GLOBAL_MIN_AGE_YEARS }), (dob) => {
      if (!dob) return true;
      return isOverMinimumAge(dob);
    }),
});

export const AttributeObjValidation = yup.object().shape({
  name: yup.string().required(t('validation.nameRequired')),
  description: yup.string(),
  isEnabled: yup.boolean(),
});

export const SubUserObjValidation = yup.object().shape({
  firstName: yup.string()
    .required(t('validation.firstNameRequired')),
  lastName: yup.string()
    .required(t('validation.lastNameRequired')),
  email: yup.string().email(t('validation.emailInvalid'))
    .required(t('validation.emailRequired')),
});
